import { Link } from '@nespresso-design-system/components';
import PriceContainer from '@nespresso/catalog-components-react/containers/PriceContainer';
import {
  buildProductUrl,
  strikethroughPriceFromProductDiscount,
  type Product
} from '@/domain/Product';
import useMarketConfig from '@/widgets/hooks/useMarketConfig';
import usePriceFormatter from '@/widgets/hooks/usePriceFormatter';
import useRoute from '@/widgets/hooks/useRoute';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductDetailsHero/config';
import styles from './BundleListItem.module.scss';
import ProductImage from '@/widgets/ProductList/cards/parts/ProductImage/ProductImage';
import ProductHeadline from '@/widgets/ProductList/cards/parts/ProductHeadline/ProductHeadline';
import { namespace } from '@/widgets/ProductDetailsHero/Providers';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions';
import type { Options } from '../../../config/index.ts';
import AddToCartButtonDisplayer from '@/widgets/components/controls/AddToCartButtonDisplayer/AddToCartButtonDisplayer.tsx';
import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing.ts';

type BundledProductProps = {
  product: Product;
};

const IMG_WIDTH = 120;

export default function BundledListItem({ product }: BundledProductProps) {
  const formatPrice = usePriceFormatter();
  const { t } = useTranslations<Translations>();
  const {
    name,
    headline,
    mainImage,
    pricing,
    discount,
    slug,
    soldSeparatelyPrice
  } = product;
  const { market, locale, productTypePath, technology } = useRoute();
  const { financialSettings } = useMarketConfig();
  const { displayNewAddToBagButton } = useWidgetOptions<Options>();

  const linkLabel = t(
    'productDetailsHero.otherWaysToShopSection.seeBundleDetailsLabel'
  );

  const url = buildProductUrl(
    market,
    locale,
    productTypePath,
    technology,
    slug
  );

  const soldSeparatelyPriceLabel = soldSeparatelyPrice
    ? t('productDetailsHero.otherWaysToShopSection.soldSeparatelyLabel', {
        price: parseFloat(soldSeparatelyPrice) * PRICE_REPRESENTATION_MULTIPLIER
      })
    : '';

  return (
    <li class={styles.item}>
      <div
        class={styles.imageWrapper}
        data-product-long-sku={product.id}
        data-product-short-sku={product.nessoft?.legacyId}
      >
        <ProductImage
          image={mainImage}
          width={IMG_WIDTH}
          imagePolicy={{
            policyId: 'small',
            targetWidth: IMG_WIDTH,
            density: 1
          }}
          class={styles.image}
          loading="lazy"
        />
      </div>
      <div class={styles.content}>
        <h3 class={styles.name}>{name}</h3>
        <ProductHeadline headline={headline} />
        <Link href={url} label={linkLabel} className={styles.link} />
        <div class={styles.purchaseSection}>
          <div class={styles.price}>
            <PriceContainer
              locale={locale}
              currencyCode={financialSettings.defaultCurrency}
              price={pricing.price}
              layoutOrder="price-first"
              strikethroughPrice={
                discount
                  ? strikethroughPriceFromProductDiscount(
                      pricing.price,
                      discount
                    )
                  : undefined
              }
              priceFormatter={(price) => formatPrice(price)}
            />
            {soldSeparatelyPriceLabel ? (
              <span class={styles.priceSeparate}>
                {soldSeparatelyPriceLabel}
              </span>
            ) : null}
          </div>
          <AddToCartButtonDisplayer
            displayNewAddToBagButton={displayNewAddToBagButton}
            product={product}
            namespace={namespace}
            class={styles.button}
          />
        </div>
      </div>
    </li>
  );
}
